'use client'

import { X } from 'lucide-react'
import type { SeatDetail, TableBookingMode } from '@/types/seating'
import { useFormatPrice } from '@/lib/timed-ui/use-format-price'
import { isUnitBookingMode } from '@/lib/seat-map/seat-request-cap'

type SeatWithSection = SeatDetail & {
  sectionName?: string
  sectionType?: string
  sectionKey?: string
  allInPrice: number
  // Stamped by the GA picker / seat resolution — a section can hold a MIX
  // of ticket types (Adult/Child GA), so the summary breaks lines out per
  // type instead of pricing the whole group off the first seat.
  ticketTypeId?: string
  ticketTypeName?: string
}

interface SectionBookingInfo {
  bookingMode?: TableBookingMode
  minOccupancy?: number
  pricingMode?: string
}

interface SeatSelectionSummaryProps {
  selectedSeats: SeatWithSection[]
  /** Every section reads 0 available: say so instead of inviting a pick. */
  soldOut?: boolean
  onRemoveSeat: (seatId: string) => void
  onRemoveSection?: (sectionKey: string) => void
  maxSeats?: number
  accessibleSections?: Set<string>
  sectionBookingModes?: Map<string, SectionBookingInfo>
  /**
   * The authoritative all-in total for the whole selection.
   *
   * Passed in rather than summed here. A fixed fee is per ORDER unless
   * EventFee.perTicket is set, so adding up per-seat all-in prices charges that
   * fee once per seat - which is what this panel did while the sticky footer and
   * the Add-to-Cart button, both fed by calculateAllInOrderTotal, showed the
   * correct lower figure. The same screen printed $516.00 in this header and
   * $511.00 on the button for three seats. Checkout charged the lower one.
   */
  orderTotal?: number
}

/** Money to the cent, so a column of shares cannot drift into $0.004 noise. */
const round2 = (n: number): number => Math.round(n * 100) / 100

/**
 * Section types that are already named for what they are.
 *
 * A seated section reads "Section Orchestra", but the panel put the same
 * prefix on every group, so a buyer who picked table 4 read "Section Table 4"
 * and a lawn ticket read "Section Lawn". Tables, booths and general admission
 * are not sections and are printed under their own name.
 */
const NAMED_WITHOUT_PREFIX = new Set(['GA', 'TABLE', 'BOOTH'])

/**
 * The heading a buyer reads above a group of seats.
 *
 * Seated sections keep the "Section" prefix, with the same leading-"section"
 * guard the checkout and confirmation pages use (SeatTooltip, CheckoutClient,
 * ConfirmationClient) so a venue that already calls a section "Section 104"
 * does not read "Section Section 104".
 *
 * `bookedAsUnit` covers a table whose type reaches us as SEATED - cart data
 * hydrated without sectionType - which the panel groups as a table from its
 * booking mode and would otherwise still have called a section.
 */
function sectionHeading(sectionName: string | undefined, sectionType?: string, bookedAsUnit?: boolean): string {
  const name = (sectionName || '').trim()
  if (!name) return 'Section'
  if (bookedAsUnit) return name
  if (sectionType && NAMED_WITHOUT_PREFIX.has(sectionType.toUpperCase())) return name
  return /^section\b/i.test(name) ? name : `Section ${name}`
}

export default function SeatSelectionSummary({
  selectedSeats,
  onRemoveSeat,
  onRemoveSection,
  maxSeats = 10,
  accessibleSections,
  sectionBookingModes,
  soldOut = false,
  orderTotal,
}: SeatSelectionSummaryProps) {
  const formatPrice = useFormatPrice()
  // Falls back to the per-seat sum only when no total was supplied, so an
  // existing caller that has not been updated still shows a number rather than
  // nothing - but every caller in this repo passes it.
  const total = orderTotal ?? selectedSeats.reduce((sum, s) => sum + (s.allInPrice ?? 0), 0)

  if (selectedSeats.length === 0) {
    return (
      <div className="p-4 text-center text-sm text-text-tertiary">
        {soldOut ? 'This event is sold out.' : 'Select seats on the map to get started'}
      </div>
    )
  }

  /**
   * The lines print FACE VALUE and the fees are broken out once underneath.
   *
   * Every line used to print that seat's all-in price, and an all-in per-seat
   * price carries the whole per-ORDER fixed fee. So the column added up to
   * more than the total above it: three $150 seats with a 10% fee, 8% tax and
   * a $5 order fee read $172.00 three times - $516.00 to anyone adding them up
   * - against a header, an Add-to-Cart button and a charge of $506.00. A buyer
   * on the screen immediately before paying could not make the numbers agree.
   *
   * Of the two honest presentations, this is the one that reconciles AND
   * matches the next screen: the checkout summary (timed-ui/checkout/
   * OrderSummary) already lists unit prices, then Subtotal, then the fees and
   * tax, then the total. The alternative - all-in lines with a negative
   * order-level correction - would reconcile too, but it asks a buyer to read
   * a discount that is not one. Face value plus one fee line is what every
   * other summary in this flow shows.
   *
   * The combined "Fees and tax" figure is derived (total minus face value)
   * because this panel is handed only the authoritative total. Checkout
   * itemises it; here it is one line, and it is exactly the difference, so
   * the column always closes on the total.
   */
  const baseSubtotal = round2(selectedSeats.reduce((sum, s) => sum + (s.price ?? 0), 0))
  /**
   * Cart data hydrated before `price` was carried has no face values. Printing
   * a column of $0.00 would be worse than the old behaviour, so those lines
   * keep their all-in prices and the panel shows the total alone.
   */
  const showFaceValues = baseSubtotal > 0
  const feesAndTax = round2(total - baseSubtotal)
  /** What one seat's line shows; null when that seat carries no price at all. */
  const lineAmount = (seat: SeatWithSection): number | null =>
    (showFaceValues ? seat.price : seat.allInPrice) ?? null

  // Determine if a seat belongs to a unit section (whole-table, variable, GA, booth)
  const isUnitSection = (seat: SeatWithSection): boolean => {
    if (seat.sectionType === 'GA' || seat.sectionType === 'BOOTH') return true
    if (seat.sectionKey && sectionBookingModes) {
      const info = sectionBookingModes.get(seat.sectionKey)
      if (info?.bookingMode === 'whole-table' || info?.bookingMode === 'variable') return true
    }
    // Fallback: TABLE sectionType without explicit sectionKey lookup
    // (e.g., hydrated from old cart data missing sectionKey)
    if (seat.sectionType === 'TABLE') return true
    return false
  }

  // Separate unit seats (GA, BOOTH, whole-table, variable) from individual seated seats
  const unitSeats: SeatWithSection[] = []
  const seatedSeats: SeatWithSection[] = []

  for (const seat of selectedSeats) {
    if (isUnitSection(seat)) {
      unitSeats.push(seat)
    } else {
      seatedSeats.push(seat)
    }
  }

  // Group unit seats (GA, BOOTH, whole-table, variable) by section key or name
  const unitGrouped = new Map<string, { sectionKey: string; sectionName: string; sectionType: string; seats: SeatWithSection[] }>()
  for (const seat of unitSeats) {
    const key = seat.sectionKey || seat.sectionName || 'General Admission'
    const sectionName = seat.sectionName || 'General Admission'
    const group = unitGrouped.get(key) || { sectionKey: key, sectionName, sectionType: seat.sectionType || 'GA', seats: [] }
    group.seats.push(seat)
    unitGrouped.set(key, group)
  }

  // Group SEATED by section + row for compact display
  const seatedGrouped = new Map<string, { sectionName: string; rowLabel: string; seats: SeatWithSection[] }>()
  for (const seat of seatedSeats) {
    const sectionName = seat.sectionName || 'Section'
    const key = `${sectionName}::${seat.rowLabel}`
    const group = seatedGrouped.get(key) || { sectionName, rowLabel: seat.rowLabel, seats: [] }
    group.seats.push(seat)
    seatedGrouped.set(key, group)
  }

  // Seats at a table booked as a unit are outside the per-order seat cap
  // (lib/seat-map/seat-request-cap), so a count against the cap stops making
  // sense once a table is in the selection: two six-seat tables read "12",
  // not "12/10" as if the buyer were over a limit the order is not over.
  const hasCapExemptSeats = selectedSeats.some(s =>
    !!s.sectionKey && isUnitBookingMode(sectionBookingModes?.get(s.sectionKey)?.bookingMode)
  )

  return (
    <div className="flex flex-col h-full">
      <div className="px-4 py-3 border-b border-surface-border">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-text-primary">
            Selected Seats ({hasCapExemptSeats ? selectedSeats.length : `${selectedSeats.length}/${maxSeats}`})
          </h3>
          <span className="text-sm font-bold text-text-primary">{formatPrice(total)}</span>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-2 space-y-1">
        {/* Unit sections (GA, BOOTH, whole-table, variable) — grouped by section, show count, single remove button */}
        {Array.from(unitGrouped.entries()).map(([key, { sectionKey, sectionName, sectionType, seats }]) => {
          const groupTotal = round2(seats.reduce((sum, s) => sum + (lineAmount(s) ?? 0), 0))
          const unitPrice = seats[0] ? lineAmount(seats[0]) ?? 0 : 0
          const isAccessible = accessibleSections?.has(sectionName)
          const isTable = sectionType === 'TABLE'
          // Booked-as-a-unit tables priced per table read as one product —
          // "$600.00 / table (6 seats)", the table's face value with the fees
          // in the one line below — not a per-seat penny split.
          const bookingMode = sectionBookingModes?.get(sectionKey)?.bookingMode
          const isPerTable = sectionBookingModes?.get(sectionKey)?.pricingMode === 'per-table'
          // A table is a table however it reached us: by type, by pricing
          // mode, or by booking mode on hydrated data with no sectionType.
          const bookedAsUnit = isPerTable || bookingMode === 'whole-table' || bookingMode === 'variable'
          // Break out per ticket type (Adult/Child GA): each type gets its
          // own line with its own count and price so a mixed group never
          // reads as "3 tickets - $30 ea" off the first seat's price.
          const byType = new Map<string, { name: string | null; seats: SeatWithSection[] }>()
          for (const s of seats) {
            const typeKey = s.ticketTypeId || ''
            const g = byType.get(typeKey) || { name: s.ticketTypeName || null, seats: [] }
            g.seats.push(s)
            byType.set(typeKey, g)
          }
          const typeGroups = Array.from(byType.values())
          const showTypeBreakout = !isPerTable && (typeGroups.length > 1 || !!typeGroups[0]?.name)
          const label = isPerTable
            ? `${formatPrice(groupTotal)} / table (${seats.length} seat${seats.length !== 1 ? 's' : ''})`
            : isTable
              ? `${seats.length} seat${seats.length !== 1 ? 's' : ''}`
              : `${seats.length} ticket${seats.length !== 1 ? 's' : ''}`
          return (
            <div key={`unit-${key}`} className="py-1.5">
              <div className="flex items-center justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <div className="text-sm font-medium text-text-primary truncate">{sectionHeading(sectionName, sectionType, bookedAsUnit)}</div>
                    {isAccessible && (
                      <span className="px-1 py-0.5 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded text-[10px] font-medium flex-shrink-0">
                        ADA
                      </span>
                    )}
                  </div>
                  {showTypeBreakout ? (
                    <div className="space-y-0.5">
                      {typeGroups.map((tg, i) => {
                        const tgUnit = tg.seats[0] ? lineAmount(tg.seats[0]) ?? 0 : 0
                        return (
                          <div key={i} className="text-xs text-text-tertiary">
                            {tg.name || 'Ticket'} &middot; {tg.seats.length} ticket{tg.seats.length !== 1 ? 's' : ''}
                            {tgUnit > 0 && <span> &middot; {formatPrice(tgUnit)} ea</span>}
                          </div>
                        )
                      })}
                    </div>
                  ) : (
                    <div className="text-xs text-text-tertiary">
                      {label}
                      {!isPerTable && unitPrice > 0 && <span> &middot; {formatPrice(unitPrice)} ea</span>}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2 ml-2">
                  <span className="text-sm font-medium text-text-primary">
                    {formatPrice(groupTotal)}
                  </span>
                  <button
                    type="button"
                    onClick={() => {
                      if (onRemoveSection) {
                        onRemoveSection(sectionKey)
                      } else {
                        seats.forEach(s => onRemoveSeat(s.seatId))
                      }
                    }}
                    className="p-1.5 min-h-[44px] min-w-[44px] flex items-center justify-center text-text-secondary hover:text-error active:text-error transition-colors rounded-lg hover:bg-error/10 active:bg-error/10"
                    aria-label={`Remove all ${sectionName} tickets`}
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          )
        })}

        {/* SEATED sections — individual rows and seat numbers */}
        {Array.from(seatedGrouped.entries()).map(([key, { sectionName, rowLabel, seats }]) => (
          <div key={key}>
            {/* The separator was typed as two hyphens, so a buyer read
                "Section Orchestra -- Row A" in the panel. En dash. */}
            <div className="text-xs text-text-tertiary font-medium py-1">{sectionHeading(sectionName)} &ndash; Row {rowLabel}</div>
            {seats.map((seat) => {
              const amount = lineAmount(seat)
              return (
              <div
                key={seat.seatId}
                className="flex items-center justify-between py-1.5 group"
              >
                <div className="flex items-center gap-2">
                  {seat.category && (
                    <span
                      className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                      style={{ backgroundColor: seat.category.color }}
                    />
                  )}
                  <span className="text-sm text-text-primary">
                    Seat {seat.seatNumber}
                  </span>
                  {seat.category && (
                    <span className="text-xs text-text-tertiary">{seat.category.name}</span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-text-primary">
                    {/* Same double-hyphen slip in the "no price" placeholder. */}
                    {amount != null ? formatPrice(amount) : '—'}
                  </span>
                  <button
                    type="button"
                    onClick={() => onRemoveSeat(seat.seatId)}
                    className="p-1.5 min-h-[44px] min-w-[44px] flex items-center justify-center text-text-secondary hover:text-error active:text-error transition-colors rounded-lg hover:bg-error/10 active:bg-error/10"
                    aria-label={`Remove seat ${seat.seatNumber}`}
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
              )
            })}
          </div>
        ))}
      </div>

      {/* The lines above are face value, so the fees are stated once here and
          the total is repeated next to them. A buyer adding up the column now
          lands on the figure in the header and on the Add-to-Cart button. */}
      <div className="px-4 py-3 border-t border-surface-border space-y-1">
        {showFaceValues && (
          <>
            <div className="flex items-center justify-between text-xs text-text-secondary">
              <span>Subtotal</span>
              <span className="font-medium text-text-primary">{formatPrice(baseSubtotal)}</span>
            </div>
            {Math.abs(feesAndTax) >= 0.005 && (
              <div className="flex items-center justify-between text-xs text-text-secondary">
                {/* Never negative on a real selection; labelled honestly if a
                    caller ever hands us a total below face value. */}
                <span>{feesAndTax > 0 ? 'Fees and tax' : 'Adjustment'}</span>
                <span className="font-medium text-text-primary">{formatPrice(feesAndTax)}</span>
              </div>
            )}
          </>
        )}
        <div className="flex items-center justify-between text-sm font-semibold text-text-primary pt-1 border-t border-surface-border">
          <span>Total</span>
          <span>{formatPrice(total)}</span>
        </div>
      </div>
    </div>
  )
}
